// Freeze and black frame detection from a playing video (thresholds follow ffmpeg's freezedetect and
// blackdetect). A small grayscale sample is taken on every check; events are counted once a condition
// has lasted the minimum duration, and their total duration is accumulated.

const SAMPLE_WIDTH = 64;
const SAMPLE_HEIGHT = 36;
// Black: at most 2 % of the pixels brighter than 10 %
const BLACK_PIXEL = 0.1 * 255;
const BLACK_RATIO = 0.98;
const BLACK_MIN_MS = 1000;
// Freeze: mean absolute difference between samples below 0.2 %
const FREEZE_DIFF = 0.002 * 255;
const FREEZE_MIN_MS = 2000;

export function format_duration (ms) {
	if (ms < 10000) return (ms / 1000).toFixed(1) + "s";
	var s = Math.round(ms / 1000);
	if (s < 60) return s + "s";
	var m = Math.floor(s / 60);
	s = s % 60;
	return m + "m " + (s < 10 ? "0" : "") + s + "s";
}

// on_event({type, since, end}) is called when an event starts to count and when it ends
// (times are performance.now() values, end is null while the event goes on)
class Detector {
	constructor (type, min_ms, on_event) {
		this.type = type;
		this.min_ms = min_ms;
		this.on_event = on_event;
		this.reset();
	}
	reset () {
		this.since = null;
		this.counted = false;
		this.count = 0;
		this.total = 0;
	}
	update (on, now) {
		if (on) {
			if (this.since === null) this.since = now;
			if (!this.counted && now - this.since >= this.min_ms) {
				this.counted = true;
				this.count++;
				if (this.on_event) this.on_event({type: this.type, since: this.since, end: null});
			}
		} else {
			this.end(now);
		}
	}
	end (now) {
		if (this.counted) {
			this.total += now - this.since;
			if (this.on_event) this.on_event({type: this.type, since: this.since, end: now});
		}
		this.since = null;
		this.counted = false;
	}
	// The current event counts only once it has lasted the minimum duration
	active () {
		return this.counted;
	}
	duration (now) {
		return this.total + (this.counted ? now - this.since : 0);
	}
}

export class FrameMonitor {
	constructor (on_event) {
		this.sample = document.createElement("canvas");
		this.sample.width = SAMPLE_WIDTH;
		this.sample.height = SAMPLE_HEIGHT;
		this.ctx = this.sample.getContext("2d", {willReadFrequently: true});
		this.previous = null;
		this.blocked = false;
		this.freeze = new Detector("freeze", FREEZE_MIN_MS, on_event);
		this.black = new Detector("black", BLACK_MIN_MS, on_event);
	}

	// Clears the counters; an event going on is ended first so it gets its end time
	reset (now) {
		this.pause(now);
		this.freeze.reset();
		this.black.reset();
	}

	// Ends the current events, e.g. when playback is paused
	pause (now) {
		this.freeze.end(now);
		this.black.end(now);
		this.previous = null;
	}

	check (video, now) {
		if (this.blocked) return;
		var data;
		try {
			this.ctx.drawImage(video, 0, 0, SAMPLE_WIDTH, SAMPLE_HEIGHT);
			data = this.ctx.getImageData(0, 0, SAMPLE_WIDTH, SAMPLE_HEIGHT).data;
		} catch (err) {
			// Cross-origin video without CORS: the frame can't be read
			this.blocked = true;
			return;
		}
		var n = SAMPLE_WIDTH * SAMPLE_HEIGHT;
		var gray = new Uint8Array(n);
		var bright = 0;
		for (var i = 0, p = 0; p < n; i += 4, p++) {
			var y = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
			gray[p] = y;
			if (y > BLACK_PIXEL) bright++;
		}
		var black = bright <= n * (1 - BLACK_RATIO);
		var frozen = false;
		if (this.previous && !black) {
			var diff = 0;
			for (var q = 0; q < n; q++) diff += Math.abs(gray[q] - this.previous[q]);
			frozen = diff / n < FREEZE_DIFF;
		}
		this.previous = gray;
		this.black.update(black, now);
		this.freeze.update(frozen, now);
	}

	text (now) {
		if (this.blocked) return "Frame check n/a";
		var part = (name, d) => name + " " + d.count + (d.count ? " (" + format_duration(d.duration(now)) + ")" : "");
		return part("Freeze", this.freeze) + " \u00b7 " + part("Black", this.black);
	}

	active () {
		return this.freeze.active() || this.black.active();
	}
}
